/**
 * Header bar for the Schedule Interview wizard.
 * Shows title, candidate avatar + name, and an info icon that opens
 * a popover with the KContactCard (social links, email, phone, job/stage).
 */
import { useMemo } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { Avatar, Popover } from '@kula-ai/components';
import { InfoCircle } from '@kula-ai/icons/dual-tone/InfoCircle';
import { KContactCard } from '@/ui-library/components/organisms/KContactCard';

interface ScheduleHeaderProps {
  title: string;
  candidateId?: number;
  candidateName?: string;
  candidateAvatar?: string;
  jobTitle?: string;
  applicationData?: Record<string, unknown>;
  stageId?: number;
}

export const ScheduleHeader = ({
  title,
  candidateId,
  candidateName,
  candidateAvatar,
  jobTitle,
  applicationData,
  stageId,
}: ScheduleHeaderProps) => {
  // Pass applicationData.contact directly — KContactCard reads name,
  // position, email, emails, local_phone_number, image_url, social from it.
  const candidate = (applicationData?.contact ?? {}) as Record<string, any>;
  const currentJob = (applicationData?.ats_job ??
    (jobTitle ? { name: jobTitle } : {})) as Record<string, any>;

  const stageDetails = useMemo(() => {
    const stages = (applicationData?.ats_job_stages as any[]) || [];
    const currentStageId =
      (applicationData?.job_stage_id as number) || stageId || 0;
    const daysInStage = (applicationData?.days_in_stage as number) || 0;
    return { stages, stageId: currentStageId, daysInStage };
  }, [applicationData, stageId]);

  const hasContactData = !!candidate?.id;

  return (
    <Box p="15px 20px" pb="0" flexShrink={0}>
      <Flex align="center" gap="10px">
        <Text
          color="secondary-900"
          fontSize="md"
          fontWeight="semibold"
          lineHeight="30px"
          flexShrink={0}
        >
          {title}
        </Text>
        {candidateName && (
          <Flex align="center" gap="8px">
            <Avatar
              src={candidateAvatar}
              name={candidateName}
              size="xs"
              shape="square"
            />
            <Text
              fontSize="md"
              fontWeight="medium"
              color="secondary-900"
              noOfLines={1}
            >
              {candidateName}
            </Text>
            {hasContactData && (
              <Popover
                side="bottom"
                align="start"
                sideOffset={8}
                className="z-[9999] max-w-[350px] rounded-lg border border-gray-200 bg-white p-6 shadow-lg"
                trigger={
                  <Flex
                    cursor="pointer"
                    align="center"
                    justify="center"
                    flexShrink={0}
                  >
                    <InfoCircle
                      width={18}
                      height={18}
                      color="var(--chakra-colors-secondary-500)"
                    />
                  </Flex>
                }
              >
                <KContactCard
                  contactId={candidateId ?? 0}
                  candidate={candidate}
                  currentJob={currentJob}
                  stageDetails={stageDetails}
                />
              </Popover>
            )}
          </Flex>
        )}
      </Flex>
    </Box>
  );
};
